import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useCallback, useContext, useMemo, } from 'react';
import branding from 'shared/app/model/branding';
import { DayTimetable, HeatingMode, IntervalTimetable, WeekTimetable, } from 'z3k-config';
import { Alert } from '@mui/material';
import { MaterialDropdown } from 'console/shared/components/fields';
import { preventDefault, useBox, usePartial } from 'shared/hooks/utils';
import { TargetTemp } from './target-temp';
import { getTempOrModeFromSchedule, getTempOrModeFromTimetable, WeekDaySchedules, } from './day-week-schedule';
import { removeTemperatureSetting } from '../../utils';
import { assertNever } from 'webui/utils/never';
import { IntervalTimetableSchedule } from './interval-timetable-schedule';
import { Z3KSettings, Z3KSettingsEditor, } from 'console/device-settings/z3k/provider/types';
import { TemperatureSettingType } from './temperature-setting-type';
import { SettingFullRow } from '../../../material-utils/settings-card/full-row';
import { c2k, removeNulls } from 'utils';
import { partial } from 'webui/utils/functions';
import { ControlWithoutValidation } from 'console/shared/components/fields/control/without-validation';
import { seemsLikeObjectId } from 'z3k-parser/lib';
const equalId = (equal, { id }) => id === equal;
export function getEditTypeFromTemperatureSettingId(temp_setting_id, day_timetables, week_timetables, interval_timetables) {
    if (temp_setting_id === HeatingMode.OFF_MODE) {
        return TemperatureSettingType.off;
    }
    if (temp_setting_id === HeatingMode.REQUEST_MODE) {
        return TemperatureSettingType.on_by_request;
    }
    if (temp_setting_id === HeatingMode.RESERVE_MODE) {
        return TemperatureSettingType.reserve;
    }
    if (!seemsLikeObjectId(temp_setting_id)) {
        return TemperatureSettingType.fix;
    }
    const predicate = partial(equalId, temp_setting_id);
    const day_timetable = day_timetables.find(predicate);
    if (day_timetable) {
        return TemperatureSettingType.day;
    }
    const week_timetable = week_timetables.find(predicate);
    if (week_timetable) {
        return TemperatureSettingType.week;
    }
    const interval_timetable = interval_timetables.find(predicate);
    if (interval_timetable) {
        return TemperatureSettingType.interval;
    }
    return null;
}
function TemperatureSettingTypeChangerImpl(props) {
    const { value: temp_setting_id, type, day_timetables, week_timetables, interval_timetables, validation_result, validation_warnings_result, heating_circuit, heating_mode, zone_index, is_boiler_zone, } = props;
    const { deviceFirmwareVersion, addIdToBlacklist } = useContext(Z3KSettings);
    const { config, replace_config: onChangeConfig } = useContext(Z3KSettingsEditor);
    const configBox = useBox(config);
    const hmBox = useBox(heating_mode);
    const hzIndexBox = useBox(zone_index);
    const onChangeHz = useCallback((config, param_key, changes) => HeatingMode.get.applySetterById(config, hmBox.current.id, {
        heating_zones: hmBox.current.heating_zones.map((hz, index) => hzIndexBox.current === index ? { ...hz, [param_key]: changes } : hz),
    }), []);
    const removeNotNeedTemperatureSettings = useCallback((config, mode_before) => {
        if (mode_before != null &&
            [
                TemperatureSettingType.week,
                TemperatureSettingType.day,
                TemperatureSettingType.interval,
            ].includes(mode_before)) {
            return removeTemperatureSetting(config, hmBox.current.heating_zones[hzIndexBox.current]
                ?.temperature_setting, addIdToBlacklist);
        }
        return config;
    }, [addIdToBlacklist]);
    const _onChangeTemperatureSetting = useCallback((config, new_id) => onChangeConfig(onChangeHz(config, 'temperature_setting', new_id)), [onChangeConfig, onChangeHz]);
    const _addDay = useCallback((config, fill_data) => DayTimetable.get.add(config, {
        temp24: fill_data ??
            new Array(24).fill(is_boiler_zone ? HeatingMode.REQUEST_MODE : c2k(20)),
    }), [is_boiler_zone]);
    const addDay = useCallback((config, fill_data) => {
        const [new_id, new_config] = _addDay(config, fill_data);
        return _onChangeTemperatureSetting(new_config, new_id);
    }, [_onChangeTemperatureSetting, _addDay]);
    const addWeek = useCallback((config, fill_data) => {
        const new_day_ids = [];
        const config_with_days = new Array(7).fill(0).reduce((current_config) => {
            const [new_id, new_config] = _addDay(current_config, fill_data);
            new_day_ids.push(new_id);
            return new_config;
        }, config);
        const [week_id, new_config] = WeekTimetable.get.add(config_with_days, {
            temp7: new_day_ids,
        });
        return _onChangeTemperatureSetting(new_config, week_id);
    }, [_onChangeTemperatureSetting, _addDay]);
    const addIntervalTimeTable = useCallback((config) => {
        const [new_id, new_config] = IntervalTimetable.get.add(config, {
            time_intervals: [],
            outside_default: c2k(20),
            setting_register: 0,
        });
        return _onChangeTemperatureSetting(new_config, new_id);
    }, [_onChangeTemperatureSetting]);
    const onChangeTemperatureSetting = useCallback((mode_before, mode_after, fill_data) => {
        //Удаляем лишние объекты
        const new_config = removeNotNeedTemperatureSettings(configBox.current, mode_before);
        switch (mode_after) {
            case TemperatureSettingType.off:
                return _onChangeTemperatureSetting(onChangeHz(new_config, 'adjusting_sensor', null), HeatingMode.OFF_MODE);
            case TemperatureSettingType.on_by_request:
                return _onChangeTemperatureSetting(new_config, HeatingMode.REQUEST_MODE);
            case TemperatureSettingType.reserve:
                return _onChangeTemperatureSetting(new_config, HeatingMode.RESERVE_MODE);
            case TemperatureSettingType.fix:
                return _onChangeTemperatureSetting(new_config, is_boiler_zone ? HeatingMode.ALWAYS_ON_MODE : c2k(20));
            case TemperatureSettingType.day: {
                return addDay(new_config, fill_data);
            }
            case TemperatureSettingType.week: {
                return addWeek(new_config, fill_data);
            }
            case TemperatureSettingType.interval: {
                return addIntervalTimeTable(new_config);
            }
            case null:
                console.error('Не выбрали тип режима для контура');
                return;
            default:
                return assertNever(mode_after);
        }
    }, [
        removeNotNeedTemperatureSettings,
        _onChangeTemperatureSetting,
        addDay,
        addWeek,
        addIntervalTimeTable,
        is_boiler_zone,
    ]);
    const predicate = usePartial(equalId, temp_setting_id);
    const day_timetable = useMemo(() => day_timetables.find(predicate), [day_timetables, predicate]);
    const week_timetable = useMemo(() => week_timetables.find(predicate), [week_timetables, predicate]);
    const interval_timetable = useMemo(() => interval_timetables.find(predicate), [interval_timetables, predicate]);
    const day_schedule_data = useMemo(() => day_timetable != null
        ? day_timetable.temp24.map(getTempOrModeFromTimetable)
        : [], [day_timetable]);
    const week_schedule_data = useMemo(() => week_timetable != null
        ? week_timetable.temp7.map((day_id) => day_timetables
            .find(partial(equalId, day_id))
            ?.temp24.map(getTempOrModeFromTimetable) ?? [])
        : [], [week_timetable, day_timetables]);
    const onClickOptimize = useCallback((e) => {
        preventDefault(e);
        onChangeTemperatureSetting(TemperatureSettingType.week, TemperatureSettingType.day, week_schedule_data[0].map(getTempOrModeFromSchedule));
    }, [onChangeTemperatureSetting, week_schedule_data]);
    const options = useMemo(() => removeNulls([
        {
            value: TemperatureSettingType.off,
            text: 'Отключено',
            help_id: 'heating_modes_zones_temp_off',
        },
        is_boiler_zone
            ? {
                value: TemperatureSettingType.on_by_request,
                text: 'Включено по запросу',
                help_id: 'heating_modes_zones_on_request',
            }
            : null,
        deviceFirmwareVersion >= 71 || !is_boiler_zone
            ? {
                value: TemperatureSettingType.fix,
                text: is_boiler_zone
                    ? 'Включено постоянно'
                    : 'Фиксированная температура',
                help_id: is_boiler_zone
                    ? 'heating_modes_zones_constant_temp'
                    : 'heating_modes_zones_target_temp',
            }
            : null,
        is_boiler_zone
            ? {
                value: TemperatureSettingType.reserve,
                text: 'Резерв',
                help_id: 'heating_modes_zones_reserve',
            }
            : null,
        !branding.show_only_interval_timetable
            ? {
                value: TemperatureSettingType.day,
                text: 'Дневное расписание',
                help_id: 'heating_modes_zones_day_temp',
            }
            : null,
        !branding.show_only_interval_timetable
            ? {
                value: TemperatureSettingType.week,
                text: 'Недельное расписание',
                help_id: 'heating_modes_zones_week_temp',
            }
            : null,
        {
            value: TemperatureSettingType.interval,
            text: 'Интервальное расписание',
            help_id: 'heating_modes_zones_temp_interval_timetable',
        },
    ]), [is_boiler_zone, deviceFirmwareVersion, heating_mode.max_modulation_level]);
    const type_field = useCallback((input_props) => (_jsx(MaterialDropdown, { ...input_props, options: options })), [options]);
    const onChangeType = useCallback((new_type) => {
        if (new_type === TemperatureSettingType.week && day_schedule_data != null) {
            onChangeTemperatureSetting(TemperatureSettingType.day, TemperatureSettingType.week, day_schedule_data.map(getTempOrModeFromSchedule));
        }
        else {
            onChangeTemperatureSetting(type, new_type, undefined);
        }
    }, [day_schedule_data, onChangeTemperatureSetting, type]);
    return (_jsxs(_Fragment, { children: [_jsx(ControlWithoutValidation, { label: "\u0412\u0430\u0440\u0438\u0430\u043D\u0442 \u0440\u0430\u0431\u043E\u0442\u044B", value: type, onChange: onChangeType, field: type_field, help_id: "heating_modes_zones" }), !is_boiler_zone &&
                heating_circuit != null &&
                type === TemperatureSettingType.fix && (_jsx(TargetTemp, { hm: heating_mode, hz_index: zone_index, hc: heating_circuit, temp_setting: temp_setting_id, validation_result: validation_result, validation_warnings_result: validation_warnings_result })), type != null &&
                [
                    TemperatureSettingType.day,
                    TemperatureSettingType.week,
                    TemperatureSettingType.interval,
                ].includes(type) && (_jsxs(SettingFullRow, { children: [_jsx(WeekDaySchedules, { hm: heating_mode, type: type, is_boiler_zone: is_boiler_zone, temp_setting_id: temp_setting_id, day_timetable: day_timetable, week_timetable: week_timetable, day_schedule_data: day_schedule_data, week_schedule_data: week_schedule_data, onClickOptimize: onClickOptimize }), type === TemperatureSettingType.interval &&
                        interval_timetable &&
                        heating_circuit && (_jsx(IntervalTimetableSchedule, { interval_timetable: interval_timetable, selected_hc: heating_circuit, is_boiler_zone: is_boiler_zone, heating_mode: heating_mode, validation_result: validation_result }))] })), validation_result && typeof validation_result === 'string' && (_jsx(SettingFullRow, { children: _jsx(Alert, { severity: "error", children: validation_result }) })), validation_warnings_result &&
                typeof validation_warnings_result === 'string' && (_jsx(SettingFullRow, { children: _jsx(Alert, { severity: "warning", children: validation_warnings_result }) }))] }));
}
export const TemperatureSettingTypeChanger = memo(TemperatureSettingTypeChangerImpl);
